import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import React, { ReactElement, useMemo } from 'react'

import { useBreakPoint } from '../../../../toolkit/src/hooks/use-break-point'
import Typography from '../../atoms/typography'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table'

/** Your existing column type */
export type DetailedTableColumn<TData> = {
  key: keyof TData
  header: string | ReactElement
  highlight?: boolean
  borderRight?: boolean
  borderLeft?: boolean
  render?: (row: TData) => JSX.Element
}

/** Nested group structure for multi-level grouping */
type NestedGroup<TData> = {
  key: string
  label: string
  data?: TData[]
  children?: NestedGroup<TData>[]
  /** how many total leaf rows are under this group (for rowSpan on desktop) */
  totalRowCount: number
}

type Props<TData> = {
  columns: DetailedTableColumn<TData>[]
  data: TData[]
  /** Single or multiple grouping keys, e.g. "meetingTime" or ["meetingTime", "center"] */
  groupBy: keyof TData | (keyof TData)[]

  headerClassName?: string
  containerClassName?: string
  displayGroupByBorder?: boolean
}

/* =====================================================================
   1) Build a tree of nested groups with groupDataRecursive
   ===================================================================== */
function groupDataRecursive<TData>(rows: TData[], keys: (keyof TData)[]): NestedGroup<TData>[] {
  if (keys.length === 0) {
    // no grouping left => everything is one group
    return [
      {
        key: '',
        label: '',
        data: rows,
        totalRowCount: rows.length
      }
    ]
  }

  const [currentKey, ...restKeys] = keys
  const map = new Map<string, TData[]>()

  // group rows by currentKey
  rows.forEach((row) => {
    const val = String(row[currentKey] ?? '')
    if (!map.has(val)) {
      map.set(val, [])
    }
    map.get(val)!.push(row)
  })

  const result: NestedGroup<TData>[] = []

  Array.from(map.entries()).forEach(([label, groupRows]) => {
    if (restKeys.length > 0) {
      // we have more grouping levels => children
      const children = groupDataRecursive(groupRows, restKeys)
      const totalRowCount = children.reduce((sum, child) => sum + child.totalRowCount, 0)
      result.push({
        key: String(currentKey),
        label,
        children,
        totalRowCount
      })
    } else {
      // last level => store data
      result.push({
        key: String(currentKey),
        label,
        data: groupRows,
        totalRowCount: groupRows.length
      })
    }
  })

  return result
}

/**
 * Recursively render the grouping rows (with rowSpan).
 * For each group at "level", we show the grouping column in [level]
 * rowSpan = totalRowCount of that group.
 * Then either we:
 *   - Recurse deeper if group has children
 *   - Or show the actual data rows if it's a leaf
 */
function renderDesktopGroups<TData extends object>({
  groups,
  groupingColumns,
  dataColumns,
  level,
  displayGroupByBorder
}: {
  groups: NestedGroup<TData>[]
  groupingColumns: DetailedTableColumn<TData>[]
  dataColumns: DetailedTableColumn<TData>[]
  level: number
  displayGroupByBorder?: boolean
}): React.ReactNode[] {
  const rows: React.ReactNode[] = []
  const isLeafLevel = level >= groupingColumns.length

  for (const group of groups) {
    if (isLeafLevel) {
      // No more grouping columns => we directly show data rows
      const rowData = group.data ?? []

      rowData.forEach((row, rowIndex) => {
        rows.push(
          <TableRow key={rowIndex}>
            {/* no grouping columns to show, so only data columns */}
            {dataColumns.map((col, colIndex) => (
              <TableCell
                key={colIndex}
                className={cn({
                  'border-r border-r-white-active': col.borderRight,
                  'border-l border-l-white-active': col.borderLeft
                })}
              >
                {col.render ? col.render(row) : String(row[col.key])}
              </TableCell>
            ))}
          </TableRow>
        )
      })
    } else {
      // We have a grouping column at [level]
      const groupCol = groupingColumns[level]
      const rowSpan = group.totalRowCount

      // The first row of this group: we put the group label in groupCol
      // If the group has children => we recursively render them.
      // If it's the last grouping column => group.data is the leaf data
      if (group.children && group.children.length > 0) {
        // We'll get the child rows from recursion
        const childRows = renderDesktopGroups({
          groups: group.children,
          groupingColumns,
          dataColumns,
          level: level + 1,
          displayGroupByBorder
        })

        if (childRows.length > 0) {
          // Insert the grouping cell in the first <tr> of childRows
          // But childRows is an array of <tr> elements, so we can clone the first one.
          const [firstChildRow, ...rest] = childRows

          rows.push(
            React.cloneElement(firstChildRow as React.ReactElement, {
              key: `firstrow-group-${group.label}`,
              children: [
                // grouping cell:
                <TableCell
                  key="group-cell"
                  rowSpan={rowSpan}
                  className={cn('items-center align-middle', {
                    'border-r border-r-white-active': displayGroupByBorder
                  })}
                >
                  {groupCol.render && group.children[0]?.data?.[0]
                    ? groupCol.render(group.children[0].data[0])
                    : group.label}
                </TableCell>,
                ...((firstChildRow as React.ReactElement).props.children as React.ReactNode[])
              ]
            }),
            ...rest
          )
        } else {
          // no child rows => do nothing or show 1 empty row
          rows.push(
            <TableRow key={`group-empty-${group.label}`}>
              <TableCell
                rowSpan={rowSpan}
                className={cn('items-center align-middle', {
                  'border-r border-r-white-active': displayGroupByBorder
                })}
              >
                {group.label}
              </TableCell>
              {/* fill the rest of columns with empty cells if you want */}
              {dataColumns.map((_, i) => (
                <TableCell key={i} />
              ))}
            </TableRow>
          )
        }
      } else {
        // leaf group => group.data
        const rowData = group.data ?? []
        if (rowData.length === 0) {
          // show 1 empty row with the group label
          rows.push(
            <TableRow key={`empty-group-${group.label}`}>
              <TableCell
                rowSpan={1}
                className={cn('items-center align-middle', {
                  'border-r border-r-white-active': displayGroupByBorder
                })}
              >
                {group.label}
              </TableCell>
              {dataColumns.map((_, i) => (
                <TableCell key={i} />
              ))}
            </TableRow>
          )
        } else {
          rowData.forEach((row, rowIndex) => {
            if (rowIndex === 0) {
              // first row => grouping cell
              rows.push(
                <TableRow key={`group-${group.label}-row-${rowIndex}`}>
                  <TableCell
                    rowSpan={rowData.length}
                    className={cn('items-center align-middle', {
                      'border-r border-r-white-active': displayGroupByBorder
                    })}
                  >
                    {groupCol.render ? groupCol.render(row) : group.label}
                  </TableCell>
                  {dataColumns.map((col, colIndex) => (
                    <TableCell
                      key={`group-${group.label}-col-${colIndex}-${rowIndex}`}
                      className={cn({
                        'border-r border-r-white-active': col.borderRight,
                        'border-l border-l-white-active': col.borderLeft
                      })}
                    >
                      {col.render ? col.render(row) : String(row[col.key])}
                    </TableCell>
                  ))}
                </TableRow>
              )
            } else {
              // subsequent row => no grouping cell
              rows.push(
                <TableRow key={`group-${group.label}-row-${rowIndex}`}>
                  {dataColumns.map((col, colIndex) => (
                    <TableCell
                      key={colIndex}
                      className={cn({
                        'border-r border-r-white-active': col.borderRight,
                        'border-l border-l-white-active': col.borderLeft
                      })}
                    >
                      {col.render ? col.render(row) : String(row[col.key])}
                    </TableCell>
                  ))}
                </TableRow>
              )
            }
          })
        }
      }
    }
  }

  return rows
}

/* =====================================================================
   2) Desktop - We produce one <Table> with grouping columns at the left 
      and data columns after. 
===================================================================== */
function DesktopGroupedTableRenderer<TData extends object>({
  columns,
  groupedData,
  groupKeys,
  displayGroupByBorder
}: {
  columns: DetailedTableColumn<TData>[]
  groupedData: NestedGroup<TData>[]
  groupKeys: (keyof TData)[]
  displayGroupByBorder?: boolean
}) {
  // reorder columns so grouping columns appear first
  const groupingColumns: DetailedTableColumn<TData>[] = []
  groupKeys.forEach((gk) => {
    const found = columns.find((c) => c.key === gk)
    if (found) groupingColumns.push(found)
  })

  // the rest of columns are "data columns"
  const dataColumns = columns.filter((c) => !groupKeys.includes(c.key))

  return (
    <TableBody>
      {/* We only produce <TableHeader> in the parent component (below).
          Here we produce the <TableRow>s for the grouped data */}
      {renderDesktopGroups({
        groups: groupedData,
        groupingColumns,
        dataColumns,
        level: 0,
        displayGroupByBorder
      })}
    </TableBody>
  )
}

function renderMobileGroups<TData extends object>(
  groups: NestedGroup<TData>[],
  columns: DetailedTableColumn<TData>[],
  groupKeys: (keyof TData)[],
  level: number
): React.ReactNode[] {
  const results: React.ReactNode[] = []
  const isLeafLevel = level >= groupKeys.length

  // When we're at the leaf level, we still want to use the last grouping column's renderer if available
  const lastGroupCol = columns.find((col) => col.key === groupKeys[groupKeys.length - 1]) || null

  for (const group of groups) {
    if (isLeafLevel) {
      const rowData = group.data ?? []
      if (rowData.length === 0) continue

      // All non-group columns
      const allDataCols = columns.filter((c) => !groupKeys.includes(c.key))
      const [pivotCol, ...valueCols] = allDataCols // ← exclude pivot from data rows

      // 1) Group title row (colored)
      results.push(
        <TableRow key={`mobile-leaf-group-${group.label}`}>
          <TableCell className="text-center bg-primary-light border-b border-b-primary-light border-r border-r-white" />
          <TableCell
            colSpan={rowData.length}
            className="text-center bg-primary-light border-b border-b-white "
          >
            {lastGroupCol?.render ? lastGroupCol.render(rowData[0]) : group.label}
          </TableCell>
        </TableRow>
      )

      // 2) Pivot header row (“1er séjour”, “2 à 4 séjours”, …)
      results.push(
        <TableRow key={`mobile-pivot-${group.label}`}>
          <TableCell className="bg-primary-light border-b border-b-white border-r border-r-white" />
          {rowData.map((r, i) => (
            <TableCell
              key={i}
              className="bg-primary-light text-center !font-semibold border-b border-b-white"
            >
              {pivotCol.render ? pivotCol.render(r) : String(r[pivotCol.key])}
            </TableCell>
          ))}
        </TableRow>
      )

      // 3) Data rows (ONLY the remaining columns)
      valueCols.forEach((col) => {
        results.push(
          <TableRow key={`m-row-${group.label}-${String(col.key)}`}>
            <TableCell className="!font-semibold text-xs border-r border-white-active text-nowrap">
              {col.header}
            </TableCell>
            {rowData.map((r, idx) => (
              <TableCell key={idx} className="text-center">
                {col.render ? col.render(r) : String(r[col.key])}
              </TableCell>
            ))}
          </TableRow>
        )
      })

      continue
    } else {
      if (group.children) {
        // Recurse deeper
        results.push(...renderMobileGroups(group.children, columns, groupKeys, level + 1))
      } else if (group.data) {
        results.push(...renderMobileGroups([group], columns, groupKeys, groupKeys.length))
      }
    }
  }

  return results
}

/* =====================================================================
   3) Mobile 
   ===================================================================== */
function MobileGroupedTableRenderer<TData extends object>({
  columns,
  groupedData,
  groupKeys,
  startLevel = 0
}: {
  columns: DetailedTableColumn<TData>[]
  groupedData: NestedGroup<TData>[]
  groupKeys: (keyof TData)[]
  startLevel?: number
}) {
  return <TableBody>{renderMobileGroups(groupedData, columns, groupKeys, startLevel)}</TableBody>
}

/* =====================================================================
   4) The main DetailedTable 
   ===================================================================== */
const DetailedTable = <TData extends object>({
  data,
  columns,
  groupBy,
  headerClassName,
  containerClassName,
  displayGroupByBorder = true
}: Props<TData>) => {
  const breakpoint = useBreakPoint()
  const groupKeys = Array.isArray(groupBy) ? groupBy : [groupBy]

  // Build nested groups
  const groupedData = useMemo(() => {
    return groupDataRecursive(data, groupKeys)
  }, [data, groupBy])
  if (breakpoint === 'mobile') {
    const topGroupCol = columns.find(
      (c) => c.key === (Array.isArray(groupBy) ? groupBy[0] : groupBy)
    )

    return (
      <>
        {/* render the grouped name ONCE, outside the table */}
        {topGroupCol?.header && (
          <Typography variant="caption" color={tailwindColors.red.DEFAULT} className="mb-2">
            {topGroupCol.header}
          </Typography>
        )}

        <Table borderRadius={16} containerClassName={containerClassName}>
          {/* start rendering from the NEXT level so we don't repeat the label */}
          <MobileGroupedTableRenderer
            columns={columns}
            groupedData={groupedData}
            groupKeys={Array.isArray(groupBy) ? groupBy : [groupBy]}
            startLevel={1}
          />
        </Table>
      </>
    )
  }

  // Desktop: a single <Table>, with grouping columns on the left.
  // We'll produce a single <TableHeader> row with all columns.
  // Then <TableBody> is built by recursion.
  return (
    <Table borderRadius={16} containerClassName={containerClassName}>
      <TableHeader>
        <TableRow>
          {/* Re-order columns so group keys appear first, then the rest. */}
          {groupKeys.map((gk) => {
            const c = columns.find((col) => col.key === gk)
            if (!c) return null
            return (
              <TableHead
                key={String(c.key)}
                className={cn('whitespace-nowrap !font-bold py-5', headerClassName)}
              >
                {c.header}
              </TableHead>
            )
          })}
          {columns
            .filter((c) => !groupKeys.includes(c.key))
            .map((c) => (
              <TableHead
                key={String(c.key)}
                className={cn('whitespace-nowrap !font-bold py-5', headerClassName)}
              >
                {c.header}
              </TableHead>
            ))}
        </TableRow>
      </TableHeader>

      <DesktopGroupedTableRenderer
        columns={columns}
        groupedData={groupedData}
        groupKeys={groupKeys}
        displayGroupByBorder={displayGroupByBorder}
      />
    </Table>
  )
}

export default DetailedTable
